import { useDispatch } from 'react-redux';
import { setActivityDrawer } from '@store/appStore';
import { buildCatalogOrigin } from '@api/ActivityLogs/CatalogActivity';
import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    IconButton,
    InputAdornment,
    Menu,
    MenuItem,
    Stack,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import useOpenModal from '@hooks/useOpenModal';
import useConfirmationModal from '@hooks/useConfirmationModal';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { showAlertMessage } from '@utils/Alerts/Message';
import { ModalSaveDish } from '@screens/Catalog/Dishes/ModalSaveDish';
import DishesService from '@api/Dishes/DishCtrl';
import { Dish, formatMargin, getDishCostScenarios, getDishMissingRecipeIds } from '@api/Dishes/Dish';
import RecipesService from '@api/Recipes/RecipeCtrl';
import { buildInputsById, Recipe } from '@api/Recipes/Recipe';
import { buildRecipesById, ModifierGroup } from '@api/ModifierGroups/ModifierGroup';
import ModifierGroupsService from '@api/ModifierGroups/ModifierGroupCtrl';
import InputsService from '@api/Inputs/InputCtrl';
import { formatUnitCost, Input } from '@api/Inputs/Input';
import ProductCategoriesService from '@api/ProductCategories/ProductCategoryCtrl';
import { ProductCategory } from '@api/ProductCategories/ProductCategory';
import Grid from '@mui/material/Grid';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SettingsIcon from '@mui/icons-material/Settings';
import { ResponsiveDataGrid, ResponsiveGridColDef } from '@components/Utilities/Tables/ResponsiveDataGrid';
import { ModalConfirmation } from '@components/Utilities/Modals/ModalConfirmation';
import { PHOTO_UPLOAD_WARNING } from '@api/Catalog/CatalogPhotos';
import { showAlertWarning } from '@utils/Alerts/Message';

const ListDishes: React.FunctionComponent = () => {
    const dishesService = useMemo(() => new DishesService(), []);
    const recipesService = useMemo(() => new RecipesService(), []);
    const inputsService = useMemo(() => new InputsService(), []);
    const modifierGroupsService = useMemo(() => new ModifierGroupsService(), []);
    const productCategoriesService = useMemo(() => new ProductCategoriesService(), []);
    const [dishes, setDishes] = useState<Dish[]>([]);
    const [recipes, setRecipes] = useState<Recipe[]>([]);
    const [inputs, setInputs] = useState<Input[]>([]);
    const [modifierGroups, setModifierGroups] = useState<ModifierGroup[]>([]);
    const [productCategories, setProductCategories] = useState<ProductCategory[]>([]);
    const [currentDish, setCurrentDish] = useState<Dish | undefined>(undefined);
    const [filters, setFilters] = useState({
        search: ''
    });
    const modalSaveDish = useOpenModal();
    const modalConfirmation = useConfirmationModal();

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedDish, setSelectedDish] = useState<Dish | undefined>(undefined);

    const inputsById = useMemo(() => buildInputsById(inputs), [inputs]);
    const recipesById = useMemo(() => buildRecipesById(recipes), [recipes]);
    // Costeo por escenario (F7): un producto configurable no tiene UN costo
    const modifierGroupsById = useMemo(
        () => new Map(modifierGroups.filter((group) => group.id).map((group) => [group.id as string, group])),
        [modifierGroups]
    );
    const categoriesById = useMemo(() => {
        const map = new Map<string, ProductCategory>();
        productCategories.forEach((category) => {
            if (category.id) {
                map.set(category.id, category);
            }
        });
        return map;
    }, [productCategories]);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, row: Dish) => {
        setAnchorEl(event.currentTarget);
        setSelectedDish(row);
    };

    const dispatch = useDispatch();

    /**
     * Abre la bitácora de este objeto (M2). Antes de esto, el catálogo escribía
     * renglones que **nadie podía ver**: no había puerta en ninguna de sus
     * pantallas.
     *
     * El snapshot se toma ANTES de cerrar el menú, que es donde se pierde la
     * selección, y el renglón de creación sale del propio documento (D8): no
     * cuesta una lectura.
     */
    const handleActivity = () => {
        const item = selectedDish;
        handleMenuClose();
        if (!item?.id) return;
        dispatch(
            setActivityDrawer({
                isOpen: true,
                section: 'dish',
                sectionId: item.id,
                derived: buildCatalogOrigin(item, 'creó el producto', 'Producto creado')
            })
        );
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleEdit = () => {
        handleMenuClose();
        if (selectedDish) {
            setCurrentDish(selectedDish);
            modalSaveDish.toggle();
        }
    };

    const handleToggleStatus = async () => {
        handleMenuClose();
        if (selectedDish?.id) {
            showLoader();
            try {
                await dishesService.toggleStatus(selectedDish.id);
                showAlertMessage('Estatus del producto actualizado exitosamente');
                await getData();
            } catch (error) {
                showAlertError(error);
            }
            hideLoader();
        }
    };

    const handleDeleteClick = () => {
        handleMenuClose();
        if (selectedDish) {
            modalConfirmation.setModalData(
                'Eliminar producto',
                `¿Estás seguro de que deseas eliminar el producto "${selectedDish.name}"? Esta acción no se puede deshacer.`
            );
        }
    };

    const handleDeleteConfirm = async () => {
        if (selectedDish?.id) {
            showLoader();
            try {
                await dishesService.deleteDish(selectedDish.id);
                showAlertMessage('Producto eliminado exitosamente');
                await getData();
            } catch (error) {
                showAlertError(error);
            }
            hideLoader();
        }
    };

    const getData = async () => {
        showLoader();
        try {
            const [dataDishes, dataRecipes, dataInputs, dataModifierGroups, dataCategories] = await Promise.all([
                dishesService.getDishes(),
                recipesService.getRecipes(),
                inputsService.getInputs(),
                modifierGroupsService.getModifierGroups(),
                productCategoriesService.getProductCategories()
            ]);
            setDishes(dataDishes);
            setRecipes(dataRecipes);
            setInputs(dataInputs);
            setModifierGroups(dataModifierGroups);
            setProductCategories(dataCategories);
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    const onOpenModalAddDish = () => {
        setCurrentDish(undefined);
        modalSaveDish.toggle();
    };

    const onSaveDish = async (dish: Dish) => {
        showLoader();
        try {
            const newData = await dishesService.saveDish(dish);
            setCurrentDish(newData);
            showAlertMessage('Producto guardado exitosamente');
            await getData();
            modalSaveDish.toggle();
        } catch (error) {
            // ⚠️ Un fallo al subir la FOTO no es un guardado fallido: el platillo
            // ya está en Firestore. Se cierra el modal y se refresca igual que en
            // el camino bueno, porque **volver a darle Guardar crearía un
            // duplicado** (el formulario no conoce el id del documento recién
            // creado). Encontrado en el re-check del 2026-09-09.
            const mensaje = error instanceof Error ? error.message : String(error);
            if (mensaje.startsWith(PHOTO_UPLOAD_WARNING)) {
                showAlertWarning(mensaje.replace(PHOTO_UPLOAD_WARNING, '').trim());
                await getData();
                modalSaveDish.toggle();
            } else {
                showAlertError(error);
            }
        }
        hideLoader();
    };

    useEffect(() => {
        void getData();
    }, []);

    const columns = useMemo<ResponsiveGridColDef<Dish>[]>(
        () => [
            {
                field: 'name',
                flex: 1.5,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Producto
                    </Typography>
                ),
                renderCell: ({ row }) => {
                    const missing = getDishMissingRecipeIds(row.components, recipesById);
                    return (
                        <Stack direction="row" spacing={1} alignItems="center" minWidth={0}>
                            <Typography noWrap>{row.name}</Typography>
                            {missing.length > 0 && (
                                <Chip size="small" color="error" label={`${missing.length} receta(s) eliminada(s)`} />
                            )}
                        </Stack>
                    );
                }
            },
            {
                field: 'productCategoryId',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Categoría
                    </Typography>
                ),
                valueGetter: ({ row }) =>
                    (row.productCategoryId && categoriesById.get(row.productCategoryId)?.name) || 'Sin categoría',
                renderCell: ({ row }) => (
                    <Typography>
                        {(row.productCategoryId && categoriesById.get(row.productCategoryId)?.name) || 'Sin categoría'}
                    </Typography>
                )
            },
            {
                field: 'cost',
                flex: 1 / 2,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Precio
                    </Typography>
                ),
                renderCell: ({ row }) => <Typography>{`$${row.cost}`}</Typography>
            },
            {
                field: 'foodCost',
                flex: 1,
                showInMobile: false,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Costo insumos
                    </Typography>
                ),
                // Costeo en vivo por ESCENARIO: sumar solo los componentes daba $0
                // en los productos que consumen todo por modificadores
                renderCell: ({ row }) => {
                    const scenarios = getDishCostScenarios(row, modifierGroupsById, recipesById, inputsById);
                    if (scenarios.isFixed) {
                        return <Typography>{formatUnitCost(scenarios.best.cost)}</Typography>;
                    }
                    return (
                        <Tooltip title="Depende de lo que elija el cliente: mínimo obligatorio con lo más barato → máximo con lo más caro">
                            <Typography>
                                {`${formatUnitCost(scenarios.best.cost)} – ${formatUnitCost(scenarios.worst.cost)}`}
                            </Typography>
                        </Tooltip>
                    );
                }
            },
            {
                field: 'margin',
                flex: 1 / 2,
                showInMobile: false,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Margen
                    </Typography>
                ),
                renderCell: ({ row }) => {
                    const scenarios = getDishCostScenarios(row, modifierGroupsById, recipesById, inputsById);
                    // Un producto de regalo tiene margen negativo POR DISEÑO: el
                    // porcentaje no informa nada (−4,727%), el costo sí
                    if (scenarios.isGiveaway) {
                        return (
                            <Tooltip title="Producto de cortesía: se informa el costo, no el porcentaje">
                                <Typography color="text.secondary">Regalo</Typography>
                            </Tooltip>
                        );
                    }
                    const peor = scenarios.worst.margin;
                    const mejor = scenarios.best.margin;
                    if (peor === undefined || mejor === undefined) {
                        return <Typography>—</Typography>;
                    }
                    // El PEOR margen manda el color: es el que puede doler
                    const color = peor < 0 ? 'error.main' : undefined;
                    return scenarios.isFixed ? (
                        <Typography color={color}>{formatMargin(mejor)}</Typography>
                    ) : (
                        <Tooltip title="Rango según lo que elija el cliente (el precio del escenario ya incluye los extras cobrados)">
                            <Typography color={color}>{`${formatMargin(peor)} – ${formatMargin(mejor)}`}</Typography>
                        </Tooltip>
                    );
                }
            },
            {
                field: 'status',
                flex: 1,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Estatus
                    </Typography>
                ),
                renderCell: ({ row }) => (
                    <Stack direction="row" spacing={1} alignItems="center">
                        {row.status ? (
                            <Chip size="small" color="success" label="Activo" />
                        ) : (
                            <Chip size="small" color="default" label="Inactivo" />
                        )}
                        {row.availableOnline && <Chip size="small" variant="outlined" label="En línea" />}
                    </Stack>
                )
            },
            {
                field: 'actions',
                flex: 1 / 2,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                headerAlign: 'center',
                align: 'center',
                renderHeader: () => <SettingsIcon sx={{ color: '#809FB8' }} />,
                renderCell: ({ row }) => (
                    <IconButton onClick={(e) => handleMenuOpen(e, row)}>
                        <MoreVertIcon />
                    </IconButton>
                )
            }
        ],
        [recipesById, inputsById, categoriesById]
    );

    return (
        <Stack height="100%" spacing={2}>
            <Box>
                <Grid container spacing={2}>
                    <Grid item xs={6} />
                    <Grid item xs={12} md={3} textAlign="right">
                        <Button variant="contained" color="primary" onClick={onOpenModalAddDish} endIcon={<AddIcon />}>
                            Nuevo producto
                        </Button>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Buscar producto"
                            value={filters.search}
                            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <SearchIcon />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                </Grid>
            </Box>
            <ResponsiveDataGrid
                searchValue={filters.search}
                rows={dishes}
                columns={columns}
                onCellDoubleClick={(cel) => {
                    setCurrentDish(cel.row as Dish);
                    modalSaveDish.toggle();
                }}
            />
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                <MenuItem onClick={handleEdit}>Editar</MenuItem>
                <MenuItem onClick={handleActivity}>Actividad</MenuItem>
                <MenuItem onClick={handleToggleStatus}>{selectedDish?.status ? 'Desactivar' : 'Activar'}</MenuItem>
                <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
                    Eliminar
                </MenuItem>
            </Menu>
            <ModalSaveDish
                modalSaveDish={modalSaveDish}
                dish={currentDish}
                recipesList={recipes}
                inputsList={inputs}
                modifierGroupsList={modifierGroups}
                productCategories={productCategories}
                onSaveDish={onSaveDish}
            />
            <ModalConfirmation modalConfirmation={modalConfirmation} onConfirmation={handleDeleteConfirm} />
        </Stack>
    );
};
export default ListDishes;
